<template>
    <CentralPanel
        class="action-infos"
        :class="{ 'bot-display': botDisplay }"
    >
        <div class="action-minions">
            <div class="acting-minion">
                <span
                    :class="action.minionAction.actingMinion.isCrypt ? 'cryptCard' : 'libraryCard'"
                    @mouseenter="hover(action.minionAction.actingMinion)"
                    @mouseleave="hover(null)"
                >
                    {{ minionName(action.minionAction.actingMinion) }} </span
                >:
                <template v-if="action.minionAction.type == MinionActionType.ActionCardFromHand">
                    {{ ActionVerb[action.minionAction.card.type as keyof typeof ActionVerb] + ' ' }}
                </template>

                <strong>{{ actions.getName(action.minionAction) }}</strong>
                <template v-if="action.minionAction.type == MinionActionType.ActionCardFromHand">
                    <DisciplineIcon
                        v-for="use in action.minionAction.usage.disciplines ?? []"
                        :key="`${use.discipline}:${use.level}`"
                        :discipline="use.discipline"
                        :level="use.level"
                    />
                    <template v-if="action.minionAction.usage.x !== undefined">
                        {{ ' X=' + action.minionAction.usage.x }}
                    </template>
                </template>
                <template v-if="action.minionAction.target">
                    on
                    <span
                        @mouseenter="hover(action.minionAction.target)"
                        @mouseleave="hover(null)"
                        >{{
                            minionName(
                                action.minionAction.target,
                                selfSecureName(action.minionAction.target),
                            )
                        }}</span
                    >
                </template>

                <template
                    v-if="
                        action.minionAction.type == MinionActionType.ActionInPlay &&
                        action.minionAction.card
                    "
                >
                    Provided By
                    <span
                        @mouseenter="hover(action.minionAction.card)"
                        @mouseleave="hover(null)"
                        >{{ action.minionAction.card.name }}</span
                    >
                </template>
            </div>

            <!-- <span>Is directed ? : {{ action.minionAction?.isDirected }}</span> -->
            <!--  <span>Target : {{ action.minionAction.target?.name }}</span> -->
            <div class="blocking-minion">
                <template v-if="blockingDecisions.length">
                    <div
                        v-for="decision in blockingDecisions"
                        :key="decision.player.oid"
                        class="block-decision"
                    >
                        <strong
                            class="inline-player-name block-decision-player"
                            :style="{ backgroundColor: decision.player.rgbaColor }"
                        >
                            {{ decision.player.shortName }}
                        </strong>
                        <template v-if="decision.minion">
                            <strong>Block</strong> with
                            <span
                                class="cryptCard"
                                @mouseenter="hover(decision.minion)"
                                @mouseleave="hover(null)"
                                >{{ minionName(decision.minion) }}</span
                            >
                        </template>
                        <strong v-else> No Block </strong>
                    </div>
                </template>
                <div
                    v-else
                    class="waiting-block-decision"
                >
                    No block declaration
                </div>
            </div>
        </div>

        <!--
        Stealth / bleed / intercept are shown for every action ( human or bot )
        so any player can adjust them as the action plays out. The block state
        and the No block button are shown to every non-active player too ; only
        the impulse sequencing below stays bot-only ( fullDisplay ).
        -->
        <div class="action-properties">
            <span class="action-property">
                <PropertyStepper
                    :readOnly="isEngine"
                    :value="action.stealth"
                    label="Stealth"
                    @change="changeProperty(ActionProperty.Stealth, $event)"
                />
            </span>

            <span
                v-if="actions.isBleed(action.minionAction) || actions.isHunt(action.minionAction)"
                class="action-property"
            >
                <PropertyStepper
                    v-if="actions.isBleed(action.minionAction)"
                    :readOnly="isEngine"
                    :value="action.bleed"
                    label="Bleed"
                    @change="changeProperty(ActionProperty.Bleed, $event)"
                />

                <PropertyStepper
                    v-if="actions.isHunt(action.minionAction)"
                    :readOnly="isEngine"
                    :value="action.hunt"
                    label="Hunt"
                    @change="changeProperty(ActionProperty.Hunt, $event)"
                />
            </span>

            <!--
            Kept in the flow but only made visible once a minion attempts the
            block, so revealing it does not shift the other steppers.
            -->
            <span
                class="action-property"
                :style="{ visibility: hasBlockingMinion ? 'visible' : 'hidden' }"
            >
                <PropertyStepper
                    :readOnly="isEngine"
                    :value="action.intercept"
                    label="Intercept"
                    @change="changeProperty(ActionProperty.Intercept, $event)"
                />
            </span>
        </div>

        <div class="action-impulse">
            <div
                v-if="!isEngine"
                class="action-buttons"
            >
                <button
                    v-if="politicalActionCard"
                    class="game-button"
                    @click="callReferendum"
                >
                    Start referendum
                </button>

                <button
                    v-if="payableActionCard"
                    class="game-button"
                    @click="payActionCost"
                >
                    Pay action cost
                </button>

                <button
                    class="game-button is-danger"
                    @click="gameMutations.ACTION_endAction.actSelf({})"
                >
                    End action
                </button>
            </div>

            <!-- A game with the rules engine : who it asks now, and the user's pass -->
            <div
                v-if="isEngine"
                class="impulse-decision"
            >
                <span class="impulse-player">
                    Impulse
                    <strong :style="{ color: action.impulsePlayer?.rgbaColor }">
                        {{ action.impulsePlayer?.shortName }}
                    </strong>
                </span>

                <!-- Enthrall's crypt draw for blood, a block attempt's toll ( see actionButtons ) -->
                <button
                    v-for="option in buttons"
                    :key="option.index"
                    class="game-button engine-pass"
                    @click="chooseEngineOption(option.index)"
                >
                    {{ option.text }}
                </button>
            </div>

            <div
                v-else-if="botDisplay || selfCanAttemptBlock()"
                class="impulse-decision"
            >
                <span
                    v-if="botDisplay"
                    class="impulse-player"
                >
                    Impulse
                    <strong
                        :style="{
                            color: action.impulsePlayer?.rgbaColor,
                        }"
                    >
                        {{ action.impulsePlayer?.shortName }}
                    </strong>
                </span>

                <button
                    v-if="selfCanAttemptBlock()"
                    :disabled="botDisplay && selfDeclinedBlock"
                    class="game-button"
                    @click="
                        gameMutations.ACTION_declareBlock.actSelf({
                            block: NO_BLOCK,
                        })
                    "
                >
                    No block
                </button>

                <button
                    v-if="botDisplay"
                    class="game-button"
                    :disabled="!selfHasImpulse || !selfCanAttemptBlock()"
                    @click="
                        gameMutations.ACTION_declareReaction.actSelf({
                            reaction: NO_REACTION,
                        })
                    "
                >
                    Pass impulse
                </button>
            </div>
        </div>
    </CentralPanel>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { usePlayersStore } from '@/client/state/players.ts'
import { gameMutations } from '@/shared/state/gameMutations.ts'
import { ACTION_TYPES, ActionVerb } from '@/shared/const/model.ts'
import {
    ActionProperty,
    ActionState,
    GameType,
    MinionActionType,
    NO_BLOCK,
    NO_REACTION,
} from '@/shared/types/state.ts'
import * as actions from '@/shared/state/minionActions.ts'
import { selfCanAttemptBlock, selfSecureName } from '@/client/state/self.ts'
import PropertyStepper from '@/client/ui/components/PropertyStepper.vue'
import DisciplineIcon from '@/client/ui/components/DisciplineIcon.vue'
import CentralPanel from '@/client/ui/ingame/topArea/central/CentralPanel.vue'
import { getBlockingDecision } from '@/shared/state/actionState.ts'
import { useGameStateStore } from '@/client/store/gameState.ts'
import { Card, LibraryCard } from '@/shared/model/Card.ts'
import { Player } from '@/shared/model/Player.ts'
import { useUIFeatures } from '@/client/game/composables/useUIFeatures.ts'
import { useGameBusStore } from '@/client/store/bus.ts'
import { actionButtons } from '@/client/engine/interaction.ts'
import { chooseEngineOption, engineShortName } from '@/client/engine/driver.ts'
import { hoverEngineCard } from '@/client/engine/cardNames.ts'

const props = defineProps<{
    action: ActionState
}>()

const gameState = useGameStateStore()
const players = usePlayersStore()

// A game with the rules engine : the engine plays the action out ( see engine/projection.ts )
const isEngine = computed(() => gameState.gameType == GameType.Engine)

// A game with the rules engine : a vampire named as its log names it ( "Morocco" ), the card
// shown on hover
function minionName(minion: Card | Player, name = minion.name): string {
    // A name hidden from the user ( "Hidden Card" ) is not shortened
    return isEngine.value && minion instanceof Card && minion.isCrypt && name == minion.name ?
            engineShortName(name)
        :   name
}

function hover(card: Card | Player | null | undefined) {
    if (isEngine.value) {
        hoverEngineCard(card instanceof Card ? card : null)
    }
}
const buttons = actionButtons
const gameBus = useGameBusStore()
const { automaticCostPaymentEnabled } = useUIFeatures()

function changeProperty(propertyName: ActionProperty, amount: number) {
    gameMutations.ACTION_changeProperty.actSelf({ propertyName, amount })
}

/**
 * "Automatic cost payment" preference ( experimental ) : action cards.
 *
 * An action's cost is paid only if it goes through ( unblocked ), so it cannot
 * be paid automatically. Instead the acting player gets a "Pay action cost"
 * button that spends the cost and shows the floating indicator above the card.
 */

// The action's card when it is an action card ( ACTION_TYPES ) with a cost the
// self player ( the acting controller ) can pay by hand.
const payableActionCard = computed<LibraryCard | null>(() => {
    if (!automaticCostPaymentEnabled.value) {
        return null
    }

    const minionAction = props.action.minionAction
    if (
        minionAction.type != MinionActionType.ActionCardFromHand ||
        minionAction.actingMinion.controller.oid != players.selfPlayer?.oid
    ) {
        return null
    }

    const card = minionAction.card
    if (!(card instanceof LibraryCard) || !card.type || !ACTION_TYPES.includes(card.type)) {
        return null
    }

    // A variable "X" cost resolves to the declared value, or 0 while undeclared,
    // in which case there is nothing to pay yet.
    const bloodCost = actions.resolveCost(card.bloodCost, minionAction.usage.x)
    const poolCost = actions.resolveCost(card.poolCost, minionAction.usage.x)
    if (bloodCost <= 0 && poolCost <= 0) {
        return null
    }

    return card
})

function payActionCost() {
    const card = payableActionCard.value
    const minionAction = props.action.minionAction
    if (!card || minionAction.type != MinionActionType.ActionCardFromHand) {
        return
    }

    const actingMinion = minionAction.actingMinion
    const bloodCost = actions.resolveCost(card.bloodCost, minionAction.usage.x)
    const poolCost = actions.resolveCost(card.poolCost, minionAction.usage.x)
    // Only the costs actually spent are shown : a mutation is rejected when the
    // minion has not enough blood or the player not enough pool.
    const bloodPaid =
        bloodCost > 0 &&
        gameMutations.changeBlood.actSelf({ card: actingMinion, amount: -bloodCost }).isValid
    const poolPaid =
        poolCost > 0 &&
        gameMutations.changePool.actSelf({
            player: actingMinion.controller,
            amount: -poolCost,
        }).isValid
    gameBus.showCardCost(card.oid, bloodPaid ? bloodCost : 0, poolPaid ? poolCost : 0)
}

const botDisplay = computed(() => props.action.minionAction.actingMinion.controller.isBot)
const politicalActionCard = computed(() =>
    actions.getPoliticalActionCard(props.action.minionAction),
)

// The political action card is what the referendum is logged as coming from
function callReferendum() {
    const card = politicalActionCard.value
    if (card) {
        gameMutations.REFERENDUM_call.actSelf({ card })
    }
}
const selfHasImpulse = computed(() => props.action.impulsePlayer == players.selfPlayer)
const selfDeclinedBlock = computed(
    () =>
        !!players.selfPlayer &&
        getBlockingDecision(gameState, players.selfPlayer)?.block == NO_BLOCK,
)

// Every recorded block decision, flattened for display : the declaring player
// and the blocking minion name, or null when they declined.
const blockingDecisions = computed(() =>
    props.action.blockingDecisions.map(decision => ({
        player: decision.player,
        minion: decision.block === NO_BLOCK ? null : decision.block,
    })),
)

// The intercept only matters once a minion is actually attempting the block.
const hasBlockingMinion = computed(() =>
    blockingDecisions.value.some(decision => decision.minion !== null),
)
</script>

<style lang="scss">
.central-panel.action-infos {
    // Rows are laid out vertically with generous spacing ; the shared surface
    // provides the outer padding. Stretch the rows so the ones using
    // space-between ( properties, minions ) can spread across the full width.
    align-items: stretch;
    gap: 1rem;
    padding: 0.5rem;

    .cryptCard {
        color: $crypt-orange;
        font-weight: bold;
    }
    .libraryCard {
        color: $library-green;
        font-weight: bold;
    }

    .action-minions {
        display: flex;
        font-size: 18px;
        gap: 0.5rem;

        // Fixed 50 - 50 split, independent of content : flex-basis 0 with equal
        // grow, and min-width 0 so a growing block list never widens the column.
        .acting-minion {
            flex: 1 1 0;
            min-width: 0;
        }

        .blocking-minion {
            flex: 1 1 0;
            min-width: 0;
            border-left: solid 1px rgba($shadow-grey, 0.4);
            padding-left: 0.5rem;
            text-align: left;

            .waiting-block-decision {
                text-align: center;
            }

            // Decisions stack ; keep each on its own line and small so the row
            // stays compact, letting a long name overflow past the ellipsis.
            .block-decision {
                font-size: 14px;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                margin-bottom: 0.25rem;

                .block-decision-player {
                    margin-right: 4px;
                }
            }
        }
    }

    .action-properties {
        display: flex;
        justify-content: space-around;
    }

    .action-property {
        display: inline-flex;
        gap: 0.25rem;
    }

    .action-impulse {
        display: flex;
        justify-content: center;
        align-items: center;

        .action-buttons {
            display: flex;
            align-items: center;
            gap: 0.5rem;
        }

        .impulse-decision {
            padding: 5px;

            .impulse-player {
                margin-right: 25px;
            }

            button {
                margin-left: 10px;
            }
        }
    }

    &.bot-display {
        .action-impulse {
            justify-content: space-between;
        }

        .action-minions {
            font-size: 18px;
        }

        // The impulse sequencing is only wired for bots ; its dotted frame
        // would look out of place around the lone No block button a human sees.
        .impulse-decision {
            border: dotted 2px $purple-grey;
        }
    }
}

// With the rules engine the central box holds the decision and the cards played too, and is
// short in a window the table's height bounds ( TOP_AREA_HEIGHT ) : the action on one line, the
// block declarations on the next, the properties and the impulse sharing the last
.central-box.engine .central-panel.action-infos {
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
    gap: 2px 0.5rem;
    padding: 0.25rem 0.5rem;

    .action-minions {
        flex: 1 1 100%;
        flex-direction: column;
        align-items: center;
        gap: 2px;
        font-size: 15px;

        .blocking-minion {
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
            gap: 0 0.75rem;
            border-left: none;
            padding-left: 0;
            font-size: 13px;

            .block-decision {
                font-size: 13px;
                margin-bottom: 0;
            }
        }
    }

    .action-properties {
        flex: 1 1 auto;
        gap: 0.75rem;
    }

    .action-impulse .impulse-decision {
        border: none;
        padding: 0;

        .impulse-player {
            margin-right: 0;
        }
    }
}
</style>
